import { CHService } from "@app/project/model";
import {
    CHDevice,
    ChildDeviceInfoObject,
    OutletInfoObject,
    SecurityFobInfo,
    SecurityZoneInfo,
    ZoneStatuses,
} from "@app/project/model/ch-device";
import { CHZWaveDevice } from "@app/project/model/ch-zwave-device";
import { ServiceMetadataService } from "@app/project/services/service-metadata.service";
import { CHDeviceClassObjectState, CHDeviceState, CHZWaveDeviceState, initialDeviceState, initialZWaveDeviceState } from "@app/project/store/clarehome.state";
import * as ProjectSelectors from "@app/project/store/selectors/projects.selectors";
import { CallState, CHApiResultState, LoadingState } from "@app/root-store/api-result.state";
import {
    ClareStatus,
    ClareStatusColors,
    offlineClareStatus,
    onlineClareStatus,
    unconfiguredClareStatus,
    unknownClareStatus,
} from "@app/shared/model/clare-status.model";
import { ClareDbCollection, ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { LocalizerService } from "@app/shared/services/localization/localizer.service";
import { createFeatureSelector, createSelector } from "@ngrx/store";

const selectState = createFeatureSelector<CHDeviceState>("chdevice");
const selectZWaveDeviceState = createFeatureSelector<CHZWaveDeviceState>("chzwavedevice");
const selectDCOState = createFeatureSelector<CHDeviceClassObjectState>("chdeviceclassobject");

const selectChDevices = (state: CHDeviceState, uuid: string) => (state?.uuid ? state : initialDeviceState(uuid));
const selectChZWaveDevices = (state: CHZWaveDeviceState, uuid: string) => (state?.uuid ? state : initialZWaveDeviceState(uuid));

export const selectChDevicesState = createSelector(
    selectState,
    ProjectSelectors.getUuid,
    (state: CHDeviceState, uuid: string): CHApiResultState<ClareHomeDbCollection<CHDevice>> => selectChDevices(state, uuid)
);
export const selectChZWaveDevicesState = createSelector(
    selectZWaveDeviceState,
    ProjectSelectors.getUuid,
    (state: CHZWaveDeviceState, uuid: string): CHApiResultState<ClareHomeDbCollection<CHZWaveDevice>> => selectChZWaveDevices(state, uuid)
);
export const getChDevices = createSelector(selectChDevicesState, (devicesState): ClareHomeDbCollection<CHDevice> => devicesState.result);
export const getChZWaveDevices = createSelector(selectChZWaveDevicesState, (devicesState): ClareHomeDbCollection<CHZWaveDevice> => devicesState.result);


export const getChDeviceByUid = (uid: string) =>
    createSelector(getChDevices, (devices: ClareHomeDbCollection<CHDevice>): CHDevice => devices?.[uid] || undefined);

const getSignalStrength = dcos => {
    if (dcos?.[DEVICE_CLASSES.INTRUSION_ZONE_CONFIG]?.result?.Properties?.signalStrength) {
        return dcos[DEVICE_CLASSES.INTRUSION_ZONE_CONFIG].result.Properties.signalStrength.Value;
    }
    if (dcos?.[DEVICE_CLASSES.INTRUSION_ZONE_CONFIG]?.error) {
        return null;
    }

    return 0;
};

const getZoneName = (dcos: Record<string, any>) => dcos?.[DEVICE_CLASSES.INTRUSION_ZONE_CONFIG]?.result?.Properties?.zoneName.Value;

export const selectDeviceClassDevices = createSelector(selectDCOState, (state: CHDeviceClassObjectState) => state || {});

export const selectDeviceClassObjectsForDevice = (uid: string, dcoName: string, propName?: string) =>
    createSelector(selectDCOState, (state: CHDeviceClassObjectState) => {
        const dcoProperties = state?.[uid]?.[dcoName]?.result?.Properties;
        return dcoProperties?.[propName] || dcoProperties || null;
    });
export const getChildDevices = (parentDevice: CHDevice) =>
    createSelector(getChDevices, (devices: ClareHomeDbCollection<CHDevice>) => {
        const childDevices: CHDevice[] = [];
        if (devices && parentDevice?.ChildUIDs) {
            for (const childUid of parentDevice.ChildUIDs) {
                childDevices.push(devices[childUid]);
            }
        }
        return childDevices;
    });

/**
 *
 * @param {CHDevice} parentDevice wattbox device
 * @returns {OutletInfoObject[]} an OutletInfoObject array of wattbox child devices
 */
export const getWattboxChildDeviceInfo = (parentDevice: CHDevice) =>
    createSelector(getChDevices, (devices: ClareHomeDbCollection<CHDevice>) => {
        const childDeviceInfo: OutletInfoObject[] = [];
        if (devices && parentDevice?.ChildUIDs) {
            for (const childUid of parentDevice.ChildUIDs) {
                childDeviceInfo.push({
                    id: childUid,
                    name: devices[childUid].Name,
                    outletNumber: devices[childUid].Properties["Outlet Number"],
                });
            }
        }
        return childDeviceInfo.sort((a, b) => a.outletNumber - b.outletNumber);
    });

/**
 *
 * @param {CHDevice} parentDevice wattbox device
 * @param service
 * @returns {ChildDeviceInfoObject[]} an ChildDeviceInfoObject array of wattbox child devices
 */
export const getChildDeviceListForEnumeratedCP = (parentDevice: CHDevice, service?: CHService) =>
    createSelector(getChDevices, (devices: ClareHomeDbCollection<CHDevice>) => {
        const childDeviceInfo: ChildDeviceInfoObject[] = [];
        if (devices && parentDevice?.ChildUIDs) {
            for (const controlPointId of Object.keys(service.controlPoints)) {
                const enumIndex = controlPointId.lastIndexOf("_");
                const number = controlPointId.substring(enumIndex + 1);
                const device = devices[service.controlPoints[controlPointId].deviceUid];
                if (
                    ServiceMetadataService.isEnumeratedCp(controlPointId).length &&
                    parentDevice.ChildUIDs.includes(device.UID) &&
                    !childDeviceInfo.some(child => child.id === device.UID) &&
                    !controlPointId.startsWith("zone_config_") &&
                    !controlPointId.startsWith("zone_") &&
                    !controlPointId.startsWith("fob_config_")
                ) {
                    childDeviceInfo.push({
                        id: device.UID,
                        name: device.Name,
                        deviceNumber: +number,
                        category: LocalizerService.getDeviceCategory(service.categories[0]),
                        type: getDeviceType(device, service),
                    });
                }
            }
        }
        return childDeviceInfo.sort((a, b) => a.deviceNumber - b.deviceNumber);
    });

export const getDeviceType = (device: CHDevice, service: CHService) => {
    let type;
    if (device.info && device.info.type) {
        type = `${device.info.manufacturer || ""} ${device.info.type || ""}`;
    } else if (device.Vendor) {
        type = `${device.Vendor} ${LocalizerService.getDeviceCategory(service.categories[0])}`;
    } else {
        type = device.ProtocolAdapterName;
    }
    return type;
};

export const getSecurityZoneByUID = (deviceUid: string) =>
    createSelector(
        getChDevices,
        selectDeviceClassDevices,
        (devices: ClareHomeDbCollection<CHDevice>, dcDevices): SecurityZoneInfo => getZoneDevice(deviceUid, devices, dcDevices || {})
    );

const deviceProperties = {
    zoneNumber: "Zone Number",
    serialNumber: "Serial Number",
    deviceType: "Device Type",
    deviceTypeCode: "Device Type Code",
};

const getZoneDevice = (
    deviceUid: string,
    devices: ClareHomeDbCollection<CHDevice>,
    dcDevices: CHDeviceClassObjectState,
    armedStatuses = {}
) => {
    const { [deviceUid]: childDevice } = devices;
    const signalStrength = dcDevices ? getSignalStrength(dcDevices[childDevice.UID]) : 0;
    const zoneStatus = getZoneStatus(dcDevices[childDevice.UID], armedStatuses);
    return {
        uid: childDevice.UID,
        name: childDevice.Name,
        deviceType: childDevice.Properties[deviceProperties.deviceType] as string,
        serialNumber: childDevice.Properties[deviceProperties.serialNumber] as string,
        zoneNumber: childDevice.Properties[deviceProperties.zoneNumber] as string,
        id: childDevice.Properties[deviceProperties.zoneNumber] as string,
        onlineStatus: getDeviceStatus(childDevice),
        lowBattery: getLowBattery(dcDevices[childDevice.UID]),
        signalStrength,
        zoneStatus,
    };
};

export const convertToSecurityZoneInfo = (device: CHDevice) => {
    if (device) {
        return {
            uid: device.UID,
            name: device.Name,
            deviceType: device.Properties[deviceProperties.deviceType] as string,
            serialNumber: device.Properties[deviceProperties.serialNumber] as string,
            zoneNumber: device.Properties[deviceProperties.zoneNumber] as string,
            id: device.Properties[deviceProperties.zoneNumber] as string,
            onlineStatus: getDeviceStatus(device),
            lowBattery: null,
            signalStrength: null,
            zoneStatus: null,
        };
    }

    return null;
};

export const getZoneListDevice = (deviceUid: string, devices: ClareHomeDbCollection<CHDevice>) => {
    const { [deviceUid]: childDevice } = devices;
    return convertToSecurityZoneInfo(childDevice);
};

export const getZoneDeviceDcoInfo = (devices: SecurityZoneInfo[], armedStatuses: Record<string, boolean>) =>
    createSelector(selectDeviceClassDevices, (deviceDcos: any): SecurityZoneInfo[] =>
        devices.map(device => ({
            ...device,
            lowBattery: getLowBattery(deviceDcos[device.uid]),
            signalStrength: deviceDcos[device.uid] ? getSignalStrength(deviceDcos[device.uid]) : 0,
            zoneStatus: getZoneStatus(deviceDcos[device.uid], armedStatuses),
            name: getZoneName(deviceDcos[device.uid]) || device.name,
        }))
    );

export const getFobDeviceDcoInfo = (devices: SecurityFobInfo[]) =>
    createSelector(selectDeviceClassDevices, (deviceDcos: any): SecurityFobInfo[] =>
        devices.map(device => ({
            ...device,
            name: getZoneName(deviceDcos[device.uid]) || device.name,
        }))
    );

const isPropertyTrue = (propName: string, dcoProperties) => dcoProperties?.hasOwnProperty(propName) && dcoProperties[propName].Value;

const getLowBattery = dco => {
    let lowBattery: "Yes" | "No" | "Unknown" = "Unknown";

    if (dco) {
        if (isPropertyTrue("lowBattery", dco[DEVICE_CLASSES.INTRUSION_ZONE_CONFIG]?.result?.Properties)) {
            lowBattery = "Yes";
        } else {
            lowBattery = "No";
        }
    }

    return lowBattery;
};

const getZoneStatus = (dco, armedStatuses): [ZoneStatuses, ClareStatusColors] => {
    let activeInArmStay = true;
    let activeInArmAway = true;
    let bypassed = false;
    let sensorStat = ZoneStatuses.unknown;

    if (dco) {
        activeInArmStay = isPropertyTrue("activeInArmStay", dco[DEVICE_CLASSES.INTRUSION_ZONE_CONFIG]?.result?.Properties);
        activeInArmAway = isPropertyTrue("activeInArmAway", dco[DEVICE_CLASSES.INTRUSION_ZONE_CONFIG]?.result?.Properties);
        const zoneState =
            ["alarmed", "troubled", "tampered", "faulted", "ready"].find(
                stat => dco[DEVICE_CLASSES.INTRUSION_ZONE]?.result?.Properties[stat]?.Value
            ) || "unknown";
        sensorStat = ZoneStatuses[zoneState];
        if (dco[DEVICE_CLASSES.INTRUSION_ZONE]?.result?.Properties?.bypassed?.Value) {
            bypassed = true;
        }
    }
    if (sensorStat !== ZoneStatuses.alarmed && !bypassed) {
        const armedState = setArmedStatus(armedStatuses);

        switch (armedState) {
            case ZoneStatuses.armedStay:
            case ZoneStatuses.armingStay:
                if (activeInArmStay) {
                    sensorStat = armedState;
                }
                break;
            case ZoneStatuses.armedAway:
            case ZoneStatuses.armingAway:
                if (activeInArmAway) {
                    sensorStat = armedState;
                }
                break;
            case ZoneStatuses.armed:
            case ZoneStatuses.arming:
                sensorStat = armedState;
                break;
            default:
                break;
        }
    }
    return [getFriendlyStatus(sensorStat, bypassed), getStatusColour(sensorStat)];
};

const getFriendlyStatus = (sensorStat: ZoneStatuses, bypassed): ZoneStatuses => {
    switch (sensorStat) {
        case ZoneStatuses.armed:
        case ZoneStatuses.armedAway:
        case ZoneStatuses.armedStay:
            return ZoneStatuses.armed;

        case ZoneStatuses.arming:
        case ZoneStatuses.armingAway:
        case ZoneStatuses.armingStay:
            return ZoneStatuses.arming;
        default:
            return ((bypassed ? "Bypassed-" : "") + sensorStat) as ZoneStatuses;
    }
};
const getStatusColour = sensorStat => {
    switch (sensorStat) {
        case ZoneStatuses.alarmed:
        case ZoneStatuses.armed:
        case ZoneStatuses.arming:
        case ZoneStatuses.armingAway:
        case ZoneStatuses.armedAway:
            return ClareStatusColors.SecurityArmedAway;
        case ZoneStatuses.armedStay:
        case ZoneStatuses.armingStay:
            return ClareStatusColors.SecurityArmedStay;
        case ZoneStatuses.troubled:
        case ZoneStatuses.tampered:
        case ZoneStatuses.faulted:
            return ClareStatusColors.SecurityFaulted;
        case ZoneStatuses.ready:
            return ClareStatusColors.SecurityReady;
        default:
            return ClareStatusColors.Unconfigured;
    }
};
const setArmedStatus = armedStatuses => {
    let armedStatus;

    if (armedStatuses?.arming) {
        if (armedStatuses?.armedStay) {
            armedStatus = ZoneStatuses.armingStay;
        } else if (armedStatuses?.armedAway) {
            armedStatus = ZoneStatuses.armingAway;
        } else {
            armedStatus = ZoneStatuses.arming;
        }
    } else if (armedStatuses?.armedStay) {
        armedStatus = ZoneStatuses.armedStay;
    } else if (armedStatuses?.armedAway) {
        armedStatus = ZoneStatuses.armedAway;
    } else if (armedStatuses?.armed) {
        armedStatus = ZoneStatuses.armed;
    }
    return armedStatus;
};

export const getChDevicesByProtocolAdapter = (protocolAdapter: string) =>
    createSelector(selectChDevicesState, (devicesState): CHDevice[] =>
        Object.values(devicesState.result || {}).filter((device: CHDevice) => device.ProtocolAdapterName === protocolAdapter)
    );

const getDeviceStatus = device => {
    switch (device.Status) {
        case 2: {
            return onlineClareStatus;
        }

        case -1: {
            return unconfiguredClareStatus;
        }

        default:
            return offlineClareStatus;
    }
};

export const getDeviceStatusCounts = createSelector(selectChDevicesState, devices => {
    let online = 0;
    let offline = 0;
    let unconfigured = 0;

    for (const uid in devices.result) {
        if (devices.result[uid]) {
            switch (getDeviceStatus(devices.result[uid])) {
                case onlineClareStatus:
                    online++;
                    break;

                case offlineClareStatus:
                    offline++;
                    break;

                case unconfiguredClareStatus:
                    unconfigured++;
                    break;

                default:
            }
        }
    }

    return { online, offline, unconfigured };
});

export const selectDeviceStatus = (uid: string) =>
    createSelector(getChDevices, (devices: ClareHomeDbCollection<CHDevice>): ClareStatus => {
        if (devices && uid) {
            return getDeviceStatus(devices[uid]);
        }

        return unknownClareStatus;
    });

export const selectPendingDeletions = createSelector(
    selectChDevicesState,
    (deviceState: CHApiResultState<ClareDbCollection<CHDevice>>): string[] => deviceState.pendingDeletions
);

export const selectDeviceClassObjectState = (deviceUid: string, dcoName: string) =>
    createSelector(
        selectDeviceClassDevices,
        (dcos): { result: any; callState: CallState<any>; error?: any } =>
            dcos?.[deviceUid]?.[dcoName] || { result: null, callState: LoadingState.init }
    );
